import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "../../../Axios";
import { Urls } from "../../../Urls";
import styles from "./RateHistoryTrend.module.css";

const formatRate = (value) => `₹${(value / 1000).toFixed(1)}K`;
const formatFullRate = (value) => `₹${Math.round(value).toLocaleString("en-IN")}`;

const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getTruckTypeParam = (truckTypes) => {
  const truckType = Array.isArray(truckTypes) ? truckTypes[0] : truckTypes;
  return truckType?.value ?? truckType?.label ?? "";
};

const getFloatAddress = (location) =>
  location?.addressLine1 || location?.location?.location || location?.location_name || "";

const getTransporterName = (float) => float?.transporter_name || "";
const getApprovedBy = (float) => float?.approved_by || "";

const RateHistoryTrend = ({ origin, destination, truckTypes }) => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [rateHistory, setRateHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const pointRefs = useRef({});
  const chartWrapRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const truckType = getTruckTypeParam(truckTypes);

    if (!origin || !destination || !truckType) {
      setRateHistory([]);
      return;
    }

    setLoading(true);
    setError(false);
    axios
      .get(
        `${Urls.spot}?field=lane_historic_rates&origin=${encodeURIComponent(
          origin
        )}&destination=${encodeURIComponent(
          destination
        )}&truck_type=${encodeURIComponent(truckType)}`
      )
      .then((res) => {
        const rows = res?.data?.historic_rates || [];
        const parsed = rows
          .map((row) => ({
            date: row.date,
            value: Number(row.median_rate ?? 0),
            floats: Array.isArray(row.floats) ? row.floats : [],
          }))
          .filter((row) => row.date)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setRateHistory(parsed);
      })
      .catch((err) => {
        console.error("Failed to fetch approved rate history:", err);
        setRateHistory([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [origin, destination, truckTypes]);

  const MIN_POINT_GAP = 55;
  const H = 160;
  const PL = 55;
  const PR = 30;
  const PT = 30;
  const PB = 40;
  const chartW = Math.max(
    (rateHistory.length - 1) * MIN_POINT_GAP,
    640 - PL - PR
  );
  const W = chartW + PL + PR;
  const chartH = H - PT - PB;

  useEffect(() => {
    if (chartWrapRef.current) {
      chartWrapRef.current.scrollLeft = chartWrapRef.current.scrollWidth;
    }
  }, [rateHistory.length]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const values = rateHistory.map((p) => p.value);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const range = max - min || 1;

  const isSinglePoint = rateHistory.length === 1;

  const toX = (index) =>
    isSinglePoint
      ? PL + chartW / 2
      : PL + (index / (rateHistory.length - 1 || 1)) * chartW;
  const toY = (value) =>
    isSinglePoint ? PT + chartH / 2 : PT + (1 - (value - min) / range) * chartH;

  const points = rateHistory.map((p, index) => ({
    ...p,
    label: formatDate(p.date),
    x: toX(index),
    y: toY(p.value),
  }));

  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPath = points.length
    ? `M${points[0].x} ${points[0].y}` +
      points.slice(1).map((p) => ` L${p.x} ${p.y}`).join("") +
      ` L${points[points.length - 1].x} ${H - PB} L${points[0].x} ${H - PB} Z`
    : "";

  const hoveredPoint = hovered !== null ? points[hovered] : null;

  const TOOLTIP_WIDTH = 320;

  const showTooltip = (index) => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const el = pointRefs.current[index];
    if (el) {
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const left = Math.min(
        Math.max(centerX - TOOLTIP_WIDTH / 2, 12),
        window.innerWidth - TOOLTIP_WIDTH - 12
      );
      setTooltipPos({ x: left, y: rect.top });
    }
    setHovered(index);
  };

  const scheduleHideTooltip = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setHovered(null);
      setTooltipPos(null);
      hideTimerRef.current = null;
    }, 120);
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>Approved rate history</span>
        <span className={styles.badge}>LAST 6 MONTHS TREND</span>
        <span className={styles.hint}>Hover a point for approval details</span>
      </div>
      <div className={styles.chartWrap} ref={chartWrapRef}>
        {loading ? (
          <div className={styles.emptyState}>Loading...</div>
        ) : error ? (
          <div className={styles.emptyState}>
            Unable to load approved rate history
          </div>
        ) : points.length === 0 ? (
          <div className={styles.emptyState}>
            No approved rate history available
          </div>
        ) : isSinglePoint ? (
          <div className={styles.singlePointCard}>
            <div className={styles.singlePointHint}>
              Only one rate approved in last 6 months
            </div>
            <div className={styles.singlePointRate}>
              {formatFullRate(points[0].value)}
            </div>
            <div className={styles.singlePointDate}>{points[0].label}</div>
            {points[0].floats.length > 0 && (
              <div className={styles.tooltipFloatList}>
                {points[0].floats.map((float, floatIndex) => (
                  <div key={floatIndex} className={styles.tooltipFloat}>
                    <i
                      className={`fa fa-map-marker ${styles.tooltipRouteIcon}`}
                    />
                    <span className={styles.tooltipRoute}>
                      <span className={styles.tooltipLane}>
                        {getFloatAddress(float.origin)} →{" "}
                        {getFloatAddress(float.destination)}
                      </span>
                      {getTransporterName(float) && (
                        <span className={styles.tooltipTransporter}>
                          <b>Transporter Name:</b> {getTransporterName(float)}
                        </span>
                      )}
                      {getApprovedBy(float) && (
                        <span className={styles.tooltipTransporter}>
                          <b>Approved by:</b> {getApprovedBy(float)}
                        </span>
                      )}
                    </span>
                    <span className={styles.tooltipFloatRate}>
                      {formatFullRate(float.approved_rate)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.svgWrap}>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              width={W}
              className={styles.svg}
            >
              <defs>
                <linearGradient
                  id="rateHistoryAreaGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#393186" stopOpacity="0.16" />
                  <stop offset="100%" stopColor="#393186" stopOpacity="0" />
                </linearGradient>
              </defs>

              <path d={areaPath} fill="url(#rateHistoryAreaGradient)" />

              <polyline
                points={polyline}
                fill="none"
                stroke="#393186"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {points.map((point, index) => (
                <line
                  key={`guide-${index}`}
                  x1={point.x}
                  y1={point.y}
                  x2={point.x}
                  y2={H - PB}
                  stroke="#393186"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity={hovered === index ? 0.35 : 0.12}
                />
              ))}

              {points.map((point, index) => (
                <g
                  key={index}
                  ref={(el) => (pointRefs.current[index] = el)}
                  onMouseEnter={() => showTooltip(index)}
                  onMouseLeave={scheduleHideTooltip}
                  style={{ cursor: "pointer" }}
                >
                  <text
                    x={point.x}
                    y={point.y - 12}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="700"
                    fill="#393186"
                    opacity={hovered === null || hovered === index ? 1 : 0.45}
                  >
                    {formatRate(point.value)}
                  </text>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={hovered === index ? 5.5 : 4}
                    fill="#FFFFFF"
                    stroke="#393186"
                    strokeWidth="2"
                  />
                  <circle cx={point.x} cy={point.y} r="12" fill="transparent" />
                  <text
                    x={point.x}
                    y={H - PB + 16}
                    textAnchor={isSinglePoint ? "middle" : "end"}
                    fontSize="9"
                    fill="#727271"
                    transform={
                      isSinglePoint
                        ? undefined
                        : `rotate(-35 ${point.x} ${H - PB + 16})`
                    }
                  >
                    {point.label}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        )}
      </div>
      {hoveredPoint &&
        tooltipPos &&
        ReactDOM.createPortal(
          <div
            className={styles.floatingTooltip}
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
            }}
            onMouseEnter={() => showTooltip(hovered)}
            onMouseLeave={scheduleHideTooltip}
          >
            <div className={styles.tooltipHeader}>
              <span className={styles.tooltipHeaderRate}>
                {formatFullRate(hoveredPoint.value)}
              </span>
              <span className={styles.tooltipDate}>{hoveredPoint.label}</span>
            </div>
            <div className={styles.tooltipFloatList}>
              {hoveredPoint.floats.map((float, floatIndex) => (
                <div key={floatIndex} className={styles.tooltipFloat}>
                  <i className={`fa fa-map-marker ${styles.tooltipRouteIcon}`} />
                  <span className={styles.tooltipRoute}>
                    <span className={styles.tooltipLane}>
                      {getFloatAddress(float.origin)} → {getFloatAddress(float.destination)}
                    </span>
                    {getTransporterName(float) && (
                      <span className={styles.tooltipTransporter}>
                        <b>Transporter Name:</b> {getTransporterName(float)}
                      </span>
                    )}
                    {getApprovedBy(float) && (
                      <span className={styles.tooltipTransporter}>
                        <b>Approved by:</b> {getApprovedBy(float)}
                      </span>
                    )}
                  </span>
                  <span className={styles.tooltipFloatRate}>
                    {formatFullRate(float.approved_rate)}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RateHistoryTrend;
